import React from 'react';
import { Calendar, ChevronRight, GitFork, Globe, Lock, Star } from 'lucide-react';
import { LoadingSpinner } from '../../components/common';
import { Repository } from '../../types';

export interface RepositoryListProps {
  loadingRepos: boolean;
  repositories: Repository[];
  filteredRepositories: Repository[];
  paginatedRepositories: Repository[];
  searchQuery: string;
  activeTab: string;
  onSelectRepository: (repo: Repository) => void;
}

export const RepositoryList: React.FC<RepositoryListProps> = ({
  loadingRepos,
  repositories,
  filteredRepositories,
  paginatedRepositories,
  searchQuery,
  activeTab,
  onSelectRepository,
}) => {
  if (loadingRepos) {
    return (
      <div className="space-y-4">
        <div className="flex flex-col items-center justify-center py-12 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
          <LoadingSpinner size="lg" />
          <p className="mt-4 text-gray-600 dark:text-gray-400">Loading repositories...</p>
        </div>
      </div>
    );
  }

  if (repositories.length === 0) {
    return (
      <div className="space-y-4">
        <div className="text-center py-12 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
          <p className="text-gray-600 dark:text-gray-400">
            {searchQuery
              ? 'No repositories match your search'
              : `No repositories found for ${activeTab}. Click "Sync Repositories" to fetch them.`}
          </p>
        </div>
      </div>
    );
  }

  if (filteredRepositories.length === 0) {
    return (
      <div className="space-y-4">
        <div className="text-center py-12 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
          <p className="text-gray-600 dark:text-gray-400">
            No repositories match your current filters. Try adjusting your filter criteria.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {paginatedRepositories.map((repo) => (
        <div
          key={repo.id}
          onClick={() => onSelectRepository(repo)}
          className="bg-white dark:bg-gray-800 rounded-lg p-6 border border-gray-200 dark:border-gray-700 hover:border-blue-500 dark:hover:border-blue-400 transition-all cursor-pointer group"
        >
          <div className="flex items-center justify-between gap-4">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-3 mb-2">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white truncate">
                  {repo.fullName}
                </h3>
                {repo.private ? (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300">
                    <Lock className="w-3 h-3" />
                    Private
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400">
                    <Globe className="w-3 h-3" />
                    Public
                  </span>
                )}
              </div>

              {repo.description && (
                <p className="text-gray-600 dark:text-gray-400 text-sm mb-3 line-clamp-2">
                  {repo.description}
                </p>
              )}

              <div className="flex items-center gap-4 text-sm text-gray-500 dark:text-gray-400">
                <span className="flex items-center gap-1">
                  <span className="w-3 h-3 rounded-full bg-blue-500"></span>
                  {repo.language || 'Unknown'}
                </span>
                {(repo.metadata?.stars || repo.stars) && (
                  <span className="flex items-center gap-1">
                    <Star className="w-4 h-4" />
                    {repo.metadata?.stars || repo.stars}
                  </span>
                )}
                {(repo.metadata?.forks || repo.forks) && (
                  <span className="flex items-center gap-1">
                    <GitFork className="w-4 h-4" />
                    {repo.metadata?.forks || repo.forks}
                  </span>
                )}
                {repo.metadata?.updatedAt && (
                  <span className="flex items-center gap-1">
                    <Calendar className="w-4 h-4" />
                    {new Date(repo.metadata.updatedAt).toLocaleDateString()}
                  </span>
                )}
              </div>
            </div>

            <ChevronRight className="w-6 h-6 text-gray-400 group-hover:text-blue-500 transition-colors shrink-0" />
          </div>
        </div>
      ))}
    </div>
  );
};
